import {
  Controller,
  useForm,
} from "react-hook-form";
import {
  MeetingGeneralInfoFormValues,
  MeetingModeEnum,
  ReviewFormData,
} from "../../CalendarsPage/SchedulingSection/form.types";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { enqueueSnackbar } from "notistack";
import {
  candidateAutoComplete,
  deleteInterview,
  getInterviewDetails,
  jobAutoComplete,
} from "@endpoints/interview.service";
import CustomInputForm from "@components/CustomInputForm/CustomInputForm";
import FormControl from "@components/FormControl/FormControl";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import regexList from "@utils/regexList";

export const meetingTypes = [
  {
    label: "Virtual meeting",
    value: MeetingModeEnum["Virtual Meeting"],
  },
  {
    label: "Face-to-face meeting",
    value: MeetingModeEnum["Face-to-Face"],
  },
  {
    label: "Telephone meeting",
    value: MeetingModeEnum["Telephone Call"],
  },
];

const meetingId = null;

type MeetingGeneralInfoSectionProps = {
  formData: ReviewFormData;
};

const MeetingGeneralInfoSection = forwardRef(
  (props: MeetingGeneralInfoSectionProps, ref) => {
    const { formData } = props;
    const {
      control,
      trigger,
      setValue,
      getValues,
      handleSubmit,
      watch,
      formState: { errors },
    } = useForm<MeetingGeneralInfoFormValues>({
      defaultValues: {
        meetingMode: meetingTypes[0]?.value,
        meetingTitle: formData?.meetingTitle,
        meetingDescription: formData?.meetingDescription,
        url: formData?.url,
        candidateDetails: formData?.candidateDetails,
        jobDetails: formData?.jobDetails,
      },
    });
    const fields = watch();
    const [candidateList, setCandidateList] = useState(null);
    const [jobList, setJobList] = useState(null);

    const [deletingEvent, setDeletingEvent] = useState(false);

    const handleDeleteEvent = async (meetingId: string) => {
      try {
        setDeletingEvent(true);
        const { success } = await deleteInterview(meetingId);
        if (success) {
          enqueueSnackbar("Interview deleted successfully", {
            variant: "success",
          });
          // setEvents((prev) => deleteEvent(meetingId, prev));
        }
      } catch (error) {
      } finally {
        setDeletingEvent(false);
      }
    };

    // Ref methods
    useImperativeHandle(ref, () => ({
      getData: () => getValues(),
      submit: () =>
        new Promise((resolve, reject) => {
          handleSubmit(
            (data) => resolve(data), // validation passed
            (errors) => reject(errors) // validation failed
          )();
        }),
    }));

    // Populating existing data on form
    useEffect(() => {
      if (meetingId) {
        getInterviewDetails(meetingId).then((response) => {
          const { success, data } = response;
          if (success) {
            setValue("candidateDetails", {
              tempFullName: data?.candidateName,
              fullName: data?.candidateName,
              // @ts-ignore
              profilePic: data?.candidateProfilePic,
              refNo: data?.candidateRefNo,
              // @ts-ignore
              candidateId: data?.candidateId,
              email: data?.candidateEmail,
            });
            setValue("jobDetails", {
              tempJobTitle: data?.jobTitle,
              title: data?.jobTitle,
              refNo: data?.jobRefNo,
              jobId: data?.jobId,
            });
            setValue("meetingTitle", data?.meetingTitle);
            // @ts-ignore
            setValue("meetingMode", data?.meetingMode || meetingTypes[0]?.value);
            if (data?.meetingMode === "Virtual Meeting") {
              setValue("url", data?.url);
            } else if (data?.meetingMode === "Face-to-Face") {
              setValue("address", data?.address);
            }
            setValue("meetingDescription", data?.meetingDescription);
          }
        });
      }
    }, [meetingId]);

    console.log("meeting mode", formData);

    return (
      <div className="flex flex-col gap-3 pr-4 mt-8">
        <div className="flex items-center justify-between">
          {meetingId ? (
            <button
              disabled={deletingEvent}
              onClick={() => handleDeleteEvent(meetingId)}
              className="flex items-center font-semibold text-xs text-red-500"
            >
              Delete Event
            </button>
          ) : null}
        </div>
        {/* Form container */}
        <div className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto no-scrollbar">
          <Controller
            name="candidateDetails"
            control={control}
            rules={{
              required: "This field is required.",
            }}
            render={({ field }) => {
              return (
                <div>
                  <CustomInputForm
                    {...field}
                    value={field?.value?.tempFullName}
                    onChange={async (e) => {
                      field.onChange({
                        ...field?.value,
                        tempFullName: e?.target?.value,
                      });
                      if (e?.target?.value) {
                        const { success, data } = await candidateAutoComplete(
                          e?.target?.value
                        );
                        if (success) {
                          setCandidateList(data);
                        }
                      }
                    }}
                    label={"Enter candidate full name"}
                    placeholder={"Search for the candidate"}
                  />
                  {candidateList && (
                    <div className="bg-white border mt-2 rounded-md">
                      {candidateList?.length ? (
                        candidateList?.map((candidate) => {
                          return (
                            <div
                              onClick={() => {
                                field.onChange({
                                  tempFullName: candidate?.fullName,
                                  ...candidate,
                                });
                                setCandidateList(null);
                                trigger("jobDetails");
                              }}
                              className="flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50"
                            >
                              <div className="flex items-center gap-2">
                                {candidate?.profilePic ? (
                                  <img
                                    src={candidate?.profilePic}
                                    className="w-6 h-6 rounded-full"
                                  />
                                ) : (
                                  <div className="w-6 h-6 rounded-full flex justify-center items-center bg-gray-400">
                                    <span className="text-sm font-semibold text-white">
                                      {candidate?.fullName?.charAt(0)}
                                    </span>
                                  </div>
                                )}
                                <span className="font-medium text-xs">
                                  {candidate?.fullName}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">
                                {candidate?.refNo}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <span className="font-medium text-xs text-gray-500 mx-2">
                          No candidate found.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            }}
          />
          <Controller
            name="jobDetails"
            control={control}
            rules={{
              required: "This field is required.",
              validate: {
                candidateValidation: () => {
                  return (
                    !!fields?.candidateDetails?.candidateId ||
                    "Please select the candidate first."
                  );
                },
              },
            }}
            render={({ field }) => {
              return (
                <div>
                  <CustomInputForm
                    value={field?.value?.tempJobTitle}
                    onChange={async (e) => {
                      trigger("jobDetails");
                      field.onChange({
                        ...field?.value,
                        tempJobTitle: e?.target?.value,
                      });
                      if (
                        e?.target?.value &&
                        fields?.candidateDetails?.candidateId
                      ) {
                        const { success, data } = await jobAutoComplete({
                          searchText: e?.target?.value,
                          candidateId: fields?.candidateDetails?.candidateId,
                        });
                        if (success) {
                          setJobList(data);
                        }
                      }
                    }}
                    label={"Enter job post or company name"}
                    placeholder={"Search for the job post or company name"}
                  />
                  {jobList && (
                    <div className="bg-white border mt-2 rounded-md">
                      {jobList?.length ? (
                        jobList?.map((job) => {
                          return (
                            <div
                              onClick={() => {
                                field.onChange({
                                  tempJobTitle: job?.title,
                                  ...job,
                                });
                                setJobList(null);
                              }}
                              className="flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50"
                            >
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-xs">
                                  {job?.title}
                                </span>
                              </div>
                              <span className="text-xss font-medium text-gray-500">
                                {job?.refNo}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <span className="font-medium text-xs text-gray-500 mx-2">
                          No job found.
                        </span>
                      )}
                    </div>
                  )}
                  {errors?.jobDetails && (
                    <span className="text-xs text-red-500 mt-1">
                      {errors?.jobDetails?.message}
                    </span>
                  )}
                </div>
              );
            }}
          />
          <FormControl
            label={"Meeting title"}
            name="meetingTitle"
            placeholder={"Enter the meeting title"}
            rules={{
              required: "This field is required.",
            }}
            control={control}
            errors={errors}
          />

          <Controller
            name="meetingMode"
            control={control}
            rules={{
              required: "This field is required.",
            }}
            render={({ field }) => {
              return (
                <div className="flex flex-col">
                  <span className="font-montserrat text-primary font-medium text-xs mb-2">
                    Meeting type
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger className="flex justify-between font-montserrat text-left text-primary font-medium text-xs mb-2 border rounded-md py-2 px-3 outline-none">
                      {meetingTypes?.find(
                        (item) => item?.value === field?.value
                      )?.label || "Select meeting mode"}
                      <ChevronDown className="w-4 h-4" color="gray" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-white" align="start">
                      {meetingTypes?.map((meetingTypeOption) => {
                        return (
                          <DropdownMenuItem
                            key={meetingTypeOption?.value}
                            className="text-xs outline-none hover:bg-gray-100 font-medium hover:cursor-pointer"
                            onSelect={() => {
                              console.log(
                                "meetingTypeOption",
                                meetingTypeOption
                              );
                              field.onChange(meetingTypeOption?.value);
                            }}
                          >
                            {meetingTypeOption?.label}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {/* <select
                  {...field}
                  defaultValue={meetingTypes[0]?.value}
                  className="outline-none text-xs font-medium border rounded-md py-2.5 px-2"
                >
                  {meetingTypes?.map((item) => {
                    return (
                      <option
                        key={item?.value}
                        className="text-xs"
                        value={item?.value}
                      >
                        {item?.label}
                      </option>
                    );
                  })}
                </select> */}
                  {errors?.meetingMode && (
                    <span className="whitespace-pre text-xs mt-2 text-orange-700">
                      {errors?.meetingMode?.message}
                    </span>
                  )}
                </div>
              );
            }}
          />

          {fields?.meetingMode === MeetingModeEnum["Virtual Meeting"] ? (
            <FormControl
              label={"Meeting URL"}
              name="url"
              placeholder={"Enter the meeting url"}
              rules={{
                required: "This field is required.",
                pattern:{
                  value:regexList.meetingURL,
                  message:"Please enter a valid meeting url"
                }
              }}
              control={control}
              errors={errors}
            />
          ) : fields?.meetingMode === MeetingModeEnum["Face-to-Face"] ? (
            <FormControl
              label={"Meeting Address"}
              name="address"
              placeholder={"Enter the meeting address"}
              rules={{
                required: "This field is required.",
              }}
              control={control}
              errors={errors}
            />
          ) : null}
          <Controller
            name="meetingDescription"
            control={control}
            rules={{
              required: "This field is required.",
            }}
            render={({ field }) => {
              return (
                <div className="flex flex-col">
                  <span className="font-montserrat text-primary font-medium text-xs mb-2">
                    Meeting description
                  </span>
                  <textarea
                    {...field}
                    className="flex-1 bg-transparent outline-none py-2.5 text-xs border p-3.5 rounded-md font-medium"
                    placeholder={"Enter the meeting description"}
                    rows={5}
                    style={{ lineHeight: "1.5" }}
                  />
                  {errors?.meetingDescription && (
                    <span className="whitespace-pre text-xs mt-2 text-orange-700">
                      {errors?.meetingDescription?.message}
                    </span>
                  )}
                </div>
              );
            }}
          />
        </div>
      </div>
    );
  }
);

export default MeetingGeneralInfoSection;
